import axios from "axios";
import React, { FormEvent } from "react";

import MFASetupStep from "./MFASetupStep";
import MFACode from "./MFACode";
import MFACodeInput from "./MFACodeInput";
import setUpAxios from "../../helperFunctions/helperFunctions";
import {
  MFA_PROVISIONING_URL,
  SUBMIT_FORM_URL,
  VALIDATION_URL,
} from "./config";

interface MFACodeFormProps {
  onSuccess?: VoidFunction;
}

interface MFACodeFormState {
  error: { message: string } | null;
  isSubmitting: boolean;
  otpKey: string;
  qrCodeUri: string;
}

interface MFAProvisioningData {
  otp_key: string;
  qrcode_uri: string;
}

interface ValidationResponse {
  valid: boolean;
}

export default class MFACodeForm extends React.Component<
  MFACodeFormProps,
  MFACodeFormState
> {
  state = {
    error: null,
    isSubmitting: false,
    otpKey: "",
    qrCodeUri: "",
  };

  componentDidMount(): void {
    setUpAxios();
    this.fetchQRData();
  }

  render(): JSX.Element {
    return (
      <>
        <p className="mb-2">
          Multi-Factor Authentication (MFA) uses 3rd party apps to generate
          verification codes for an additional layer of security.
        </p>

        <MFASetupStep
          stepNumber={1}
          title="Use an authenticator app to scan this QR code"
        >
          <p className="mb-2">
            If you don&apos;t have an authenticator app, you can try Google
            Authenticator (
            <a
              href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2"
              target="_blank"
              rel="noreferrer"
            >
              Android
            </a>
            ,{" "}
            <a
              href="https://apps.apple.com/us/app/google-authenticator/id388497605"
              target="_blank"
              rel="noreferrer"
            >
              iOS
            </a>
            ) or Authy (
            <a
              href="https://play.google.com/store/apps/details?id=com.authy.authy"
              target="_blank"
              rel="noreferrer"
            >
              Android
            </a>
            ,{" "}
            <a
              href="https://apps.apple.com/us/app/twilio-authy/id494168017"
              target="_blank"
              rel="noreferrer"
            >
              iOS
            </a>
            ).
          </p>
          {this.hasMFAData && (
            <MFACode otpKey={this.state.otpKey} uri={this.state.qrCodeUri} />
          )}
        </MFASetupStep>

        <MFASetupStep stepNumber={2} title="Enter the 6-digit code below">
          <p className="mb-2">
            Once you&apos;ve scanned the QR code or entered the key, the app
            will generate a 6-digit code. Enter the 6-digit code below and click
            &quot;verify&quot; to complete the setup.
          </p>
          <MFACodeInput
            error={this.state.error}
            isSubmitting={this.state.isSubmitting}
            onSubmit={this.handleSubmit}
          />
        </MFASetupStep>
      </>
    );
  }

  fetchQRData = (): void => {
    axios.get<MFAProvisioningData>(MFA_PROVISIONING_URL).then((res) => {
      this.setState({
        otpKey: res.data.otp_key,
        qrCodeUri: res.data.qrcode_uri,
      });
    });
  };

  handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();

    this.setState({ isSubmitting: true });

    const formData = new FormData(e.target as HTMLFormElement);
    const otpAttempt = formData.get("otp_attempt");

    const validateData = {
      otp_attempt: otpAttempt,
      otp_key: this.state.otpKey,
    };

    const validationRes = await axios.post<ValidationResponse>(
      VALIDATION_URL,
      validateData
    );

    if (!validationRes.data.valid) {
      return this.setState({
        error: new Error(
          "Please check your app for a new code and try again. Codes refresh every 30 seconds."
        ),
        isSubmitting: false,
      });
    }

    this.setState({ error: null });

    const enableData = {
      otp_key: this.state.otpKey,
    };

    const enableRes = await axios.post(SUBMIT_FORM_URL, enableData);

    if (this.isError(enableRes.status)) {
      return this.setState({
        error: new Error("An unknown error occurred"),
        isSubmitting: false,
      });
    }

    this.setState({ isSubmitting: false });

    if (this.props.onSuccess) {
      this.props.onSuccess();
    }
  };

  isError = (status: number): boolean => {
    return status >= 400;
  };

  get hasMFAData(): boolean {
    return Boolean(this.state.otpKey && this.state.qrCodeUri);
  }
}
